import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BookOpenIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  DocumentCheckIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  HomeIcon,
  InboxIcon,
  LinkIcon,
  QuestionMarkCircleIcon,
  ReceiptRefundIcon,
  ShoppingCartIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useLocalStorage, useWindowSize } from 'usehooks-ts';

import { ENV } from 'services/environment/environment';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useHasRelationshipForDocumentType } from 'services/repositories/relationships/relationships';
import { useConnectionsStats, useFailedTransactionsCount } from 'services/repositories/stats/stats';
import { useCurrentUser } from 'services/repositories/user/user';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { getDocumentationLink } from 'support/helpers/const/const';
import { FEATURE_FLAGS, usePartnerFeatureFlag } from 'support/helpers/featureFlags/featureFlags';
import { FilterCompoundValue } from 'support/helpers/filters/filters';
import { classNames, isDefined } from 'support/helpers/generic/generic';
import {
  CompanyType,
  ConnectionsRoutes,
  DashboardRoutes,
  DocumentType,
  InvoiceVerificationRoutes,
  MetabaseRoutes,
  NetworkRoutes,
  OrderFormsRoutes,
  PaymentReconciliationRoutes,
  PriceCatalogRoutes,
  PricingConfigRoutes,
  RelationshipActionRequired,
  RelationshipState,
  SettingsRoutes,
  TransactionsRoutes,
} from 'support/types';
import { SidebarKeys } from 'support/types/localStorage';
import { ConnectionsFilterKey } from 'pages/Connections/tableConfig';
import { ApplicationLogo } from 'components/Logo/ApplicationLogo/ApplicationLogo';
import { SidebarProfile } from 'components/Page/SidebarProfile/SidebarProfile';

import { NavigationLink, NavigationLinkGroup } from '../types';
import { NavigationSidebarItem } from './components/NavigationSidebarItem/NavigationSidebarItem';

export const NavigationSidebar = memo(() => {
  const { width = 0 } = useWindowSize();
  const { t, i18n } = useTranslation();
  const { track } = useAnalytics();
  const { data: failedTransactionsCountData } = useFailedTransactionsCount();
  const { data: currentUser } = useCurrentUser();
  const { enabled: priceCatalogsEnabled } = usePartnerFeatureFlag(FEATURE_FLAGS.TEMPORARY_PRICE_CATALOGS);
  const [expanded, setExpanded] = useLocalStorage(SidebarKeys.open, true);
  const { data: connectionsCountData } = useConnectionsStats({
    variables: {
      query: {
        filterValues: [
          {
            key: ConnectionsFilterKey.actionRequired,
            value:
              'READY_FOR_TESTING;HAS_TEST_MESSAGES;IN_REVIEW;TASKS_PENDING' satisfies FilterCompoundValue<RelationshipActionRequired>,
          },
          { key: ConnectionsFilterKey.deactivated_partners, value: 'false' },
        ],
      },
    },
  });

  const hasMetabaseDashboard = !!currentUser?.partner?.metabase_dashboard_url;

  const hasAutoCollapsed = useRef(false);
  useEffect(() => {
    if (!hasAutoCollapsed.current && width > 0 && width < 1280) {
      setExpanded(false);
      hasAutoCollapsed.current = true;
    }
  }, [width, setExpanded]);

  const toggleExpanded = () => {
    setExpanded((expanded) => !expanded);
  };

  const { data: failedTransactions } = useFailedTransactionsCount();
  const showPaymentReconciliationTab = useShouldShowPaymentReconciliation();
  const showInvoiceVerificationTab = useShouldShowInvoiceChecks();
  const showOrderFormsTab = useFeatureFlagEnabled(FEATURE_FLAGS.ORDER_FORMS_PITCH);
  const showPaymentReconciliationPitchTab = useShouldShowPaymentReconciliationPitch();

  const linkGroups: Array<NavigationLinkGroup> = [
    {
      links: [
        { href: DashboardRoutes.root, label: t('common:mainMenu.dashboard'), icon: <HomeIcon /> },
        {
          href: TransactionsRoutes.root,
          label: t('common:mainMenu.transactions'),
          icon: <InboxIcon />,
          ...(!!failedTransactionsCountData?.failedTransactions && {
            icon: <InboxIcon />,
            value: `${failedTransactionsCountData.failedTransactions}`,
          }),
          badgeContent: failedTransactions?.failedTransactions,
        },
        showPaymentReconciliationTab || showPaymentReconciliationPitchTab
          ? {
              href: showPaymentReconciliationTab ? PaymentReconciliationRoutes.root : PaymentReconciliationRoutes.pitch,
              label: t('common:mainMenu.paymentReconciliation'),
              icon: <ReceiptRefundIcon />,
              ...(showPaymentReconciliationPitchTab &&
                !showPaymentReconciliationTab && {
                  badgeContent: t('common:mainMenu.newBadge'),
                  badgeVariant: 'new' as const,
                }),
            }
          : null,
        showInvoiceVerificationTab
          ? {
              href: InvoiceVerificationRoutes.root,
              label: t('common:mainMenu.invoiceVerification'),
              icon: <DocumentCheckIcon />,
            }
          : null,
        ...(priceCatalogsEnabled
          ? [{ href: PriceCatalogRoutes.root, label: t('common:mainMenu.priceCatalogs'), icon: <BookOpenIcon /> }]
          : [{ href: PricingConfigRoutes.root, label: t('common:mainMenu.priceLists'), icon: <TagIcon /> }]),
        showOrderFormsTab
          ? {
              href: OrderFormsRoutes.pitch,
              label: t('common:mainMenu.orderForms'),
              icon: <ShoppingCartIcon />,
              badgeContent: t('common:mainMenu.newBadge'),
              badgeVariant: 'new' as const,
            }
          : null,
        {
          href: ConnectionsRoutes.root,
          label: t('common:mainMenu.connections'),
          icon: <LinkIcon />,
          badgeContent: connectionsCountData?.connectionsCount,
        },
        { href: NetworkRoutes.root, label: t('common:mainMenu.tradePartners'), icon: <GlobeAltIcon /> },
      ].filter(isDefined),
    },
    {
      links: [
        { href: SettingsRoutes.root, label: t('common:mainMenu.settings'), icon: <Cog6ToothIcon /> },
        ...(hasMetabaseDashboard
          ? [{ href: MetabaseRoutes.root, label: t('common:mainMenu.metabase'), icon: <ChartBarIcon /> }]
          : []),
      ].filter(isDefined),
    },
    {
      links: [
        {
          href: getDocumentationLink(i18n.language),
          externalLink: true,
          onClick: () => track('documentation_opened'),
          label: t('common:mainMenu.help'),
          icon: <QuestionMarkCircleIcon />,
        },
        {
          href: ENV.VITE_PYLON_PORTAL_URL,
          label: t('common:mainMenu.emailUs.button'),
          icon: <EnvelopeIcon />,
          externalLink: true,
        },
      ],
    },
  ];

  return (
    <div className="flex h-full flex-col border-r border-gray-200">
      <div className={classNames('flex flex-col h-0 flex-1 bg-gray-50 flex-shrink-0', expanded ? 'w-64' : 'w-[72px]')}>
        <div className="flex flex-1 flex-col justify-between overflow-y-auto pb-4 pt-5">
          <div className={classNames('flex-1 flex flex-col', expanded ? ' px-6' : 'items-center')}>
            <div className="flex shrink-0 items-center pb-2">
              <ApplicationLogo
                variant="dark"
                withText={expanded}
                className={classNames('w-auto -mx-1.5', expanded ? 'h-8' : 'h-4')}
              />
            </div>

            {linkGroups.map((linkGroup, linkGroupIndex) => (
              <div key={linkGroupIndex}>
                <div className="flex flex-col space-y-1 pt-3">
                  {linkGroup.links.map((item: NavigationLink) => (
                    <NavigationSidebarItem
                      href={item.href}
                      key={item.href || item.label}
                      expanded={expanded}
                      onClick={item.onClick}
                      label={item.label}
                      icon={item.icon}
                      externalLink={item.externalLink}
                      badgeContent={item.badgeContent}
                      badgeVariant={item.badgeVariant}
                    />
                  ))}
                </div>
                {linkGroupIndex + 1 !== linkGroups.length && (
                  <hr className="-mx-2 mt-4 border-t border-gray-200" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
          <div className={classNames('flex flex-shrink-0 px-4', expanded ? '' : 'justify-center')}>
            <NavigationSidebarItem
              onClick={toggleExpanded}
              icon={expanded ? <ArrowLeftIcon /> : <ArrowRightIcon />}
              label={expanded ? t('common:mainMenu.collapse') : t('common:mainMenu.expand')}
              expanded={expanded}
            ></NavigationSidebarItem>
          </div>
        </div>
        <SidebarProfile open={expanded} />
      </div>
    </div>
  );
});

NavigationSidebar.displayName = 'Sidebar';

const useShouldShowPaymentReconciliation = () => {
  const reconciliationEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.RECONCILIATION);
  const { data: currentPartner } = useCurrentPartner();
  const hasRemittanceAdviceRelationship = useHasRelationshipForDocumentType(
    DocumentType.remittanceAdvice,
    RelationshipState.READY,
  );

  return (
    hasRemittanceAdviceRelationship &&
    reconciliationEnabled &&
    currentPartner?.companyType?.includes(CompanyType.SUPPLIER)
  );
};

const useShouldShowInvoiceChecks = () => {
  const flagEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.INVOICE_VERIFICATION);
  return flagEnabled;
};

const useShouldShowPaymentReconciliationPitch = () => {
  const pitchEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.RECONCILIATION_PITCH);
  return pitchEnabled;
};
